import { Item, SimpleSelect } from '../UIComponents2/Select/SimpleSelect'
import { AudioQualityPresetCode } from '../services/Constants'
import { useMemo, useState } from 'react'
import { isElectron } from '../utlis/isElectron'
import { useRoomContext } from '@/RoomContext'
import { useIsProUser } from '@/services/useIsProUser'
import { useTranslation } from 'react-i18next'
import { DialogTitle, DialogBody } from '@/components/Dialog'
import DialogActionButtonsSection from '@/dialogs/components/DialogActionButtonsSection'
import AlertIcon from '@/CustomIcons/AlertIcon'
import { Dialog } from '@/components/Dialog'
import { cn } from '@/utlis/cn'
import { UnlockWithPro } from './UnlockWithPro'
import ForwardIcon from '@/CustomIcons/ForwardIcon'
import { TFunction } from 'i18next'

type AudioQualitySelectProps = {
    value: AudioQualityPresetCode
    onChange: (value: AudioQualityPresetCode) => void
    className?: string
    channelGroupsShared?: number
    triggerClassName?: string
    disabled?: boolean
    isTalkback?: boolean
    label?: string
    widthMatching?: boolean
}
export function AudioQualitySelect(props: AudioQualitySelectProps) {
    const { t } = useTranslation()
    const [roomState] = useRoomContext()
    const isProUser = useIsProUser()
    const isApp = isElectron()
    const allowPro = isProUser || roomState.roomTierPolicy.allowAudioEngineTx

    const [open, setOpen] = useState(false)
    const [showFeedbackWarning, setShowFeedbackWarning] = useState(false)
    const handleAcceptWarning = () => {
        setShowFeedbackWarning(false)
        setOpen(true)
    }
    const handleCloseWarning = () => {
        setShowFeedbackWarning(false)
    }
    const handleOpen = (open: boolean) => {
        if (props.isTalkback && open) {
            setShowFeedbackWarning(true)
        } else {
            setOpen(open)
        }
    }

    const locked = !allowPro && isApp
    const proDisabled = !isApp
    const stdDisabled = (props.channelGroupsShared ?? 0) > 1

    const infoItem: any = []
    if (proDisabled) {
        infoItem.push(
            <a
                key="download_app_link"
                href="https://musesessions.co/download"
                className={cn(
                    'flex items-center gap-2 cursor-pointer t-base-light leading-[20px] text-[#FCDD40] px-[16px] py-2'
                )}
                target="_blank"
            >
                <ForwardIcon />
                <span>{t('Download app to use pro audio')}</span>
            </a>
        )
    } else if (locked) {
        infoItem.push(
            <UnlockWithPro key="muse_pro_link" reason="premiumFeature:useProAudio" className="px-[16px] py-2" />
        )
    }

    const items = useMemo(
        () =>
            [
                {
                    label: t('Standard Audio'),
                    kind: 'group',
                    items: [
                        {
                            value: 'voice',
                            label: qualityPresetToLabel('voice', t),
                            description: qualityPresetToDescription('voice', t),
                            disabled: stdDisabled,
                            tooltip: stdDisabled ? 'Too many channels are shared to select this quality' : undefined,
                        },
                        {
                            value: 'enhanced',
                            label: qualityPresetToLabel('enhanced', t),
                            description: qualityPresetToDescription('enhanced', t),
                            disabled: stdDisabled,
                            tooltip: stdDisabled ? 'Too many channels are shared to select this quality' : undefined,
                        },
                    ],
                },
                ...infoItem,
                {
                    label: t('Professional Audio (Compressed)'),
                    kind: 'group',
                    items: [
                        {
                            value: 'pro_essential',
                            label: qualityPresetToLabel('pro_essential', t),
                            description: qualityPresetToDescription('pro_essential', t),
                            locked,
                            disabled: proDisabled,
                        },
                        {
                            value: 'pro_enhanced',
                            label: qualityPresetToLabel('pro_enhanced', t),
                            description: qualityPresetToDescription('pro_enhanced', t),
                            locked,
                            disabled: proDisabled,
                        },
                        {
                            value: 'pro_hd',
                            label: qualityPresetToLabel('pro_hd', t),
                            description: qualityPresetToDescription('pro_hd', t),
                            locked,
                            disabled: proDisabled,
                        },
                    ],
                },
                {
                    label: t('Professional Audio (Uncompressed)'),
                    kind: 'group',
                    items: [
                        {
                            value: 'pro_cd',
                            label: qualityPresetToLabel('pro_cd', t),
                            description: qualityPresetToDescription('pro_cd', t),
                            locked,
                            disabled: proDisabled,
                        },
                        {
                            value: 'pro_studio',
                            label: qualityPresetToLabel('pro_studio', t),
                            description: qualityPresetToDescription('pro_studio', t),
                            locked,
                            disabled: proDisabled,
                        },
                        {
                            value: 'pro_highest',
                            label: qualityPresetToLabel('pro_highest', t),
                            description: qualityPresetToDescription('pro_highest', t),
                            locked,
                            disabled: proDisabled,
                        },
                    ],
                },
            ] as Item[],
        [locked, proDisabled, stdDisabled]
    )

    return (
        <>
            <SimpleSelect
                label={props.label}
                disabled={props.disabled}
                items={items}
                value={props.value}
                onChange={(v) => props.onChange((v as AudioQualityPresetCode) ?? 'voice')}
                className={props.className}
                triggerClassName={props.triggerClassName}
                contentClassName="w-[350px]"
                onOpenChange={handleOpen}
                open={open}
                widthMatching={props.widthMatching}
            />
            <AudioFeedbackWarning
                open={showFeedbackWarning}
                onClose={handleCloseWarning}
                onAccept={handleAcceptWarning}
            />
        </>
    )
}

export function qualityPresetToLabel(code: AudioQualityPresetCode, t: TFunction<'translation'>) {
    switch (code) {
        case 'voice':
            return t('Voice')
        case 'enhanced':
            return t('High Quality (Variable)')
        case 'pro_essential':
            return t('Low Quality')
        case 'pro_enhanced':
            return t('High Quality')
        case 'pro_hd':
            return t('Highest Quality')
        case 'pro_cd':
            return t('High Quality')
        case 'pro_studio':
            return t('Higher Quality')
        case 'pro_highest':
            return t('Best Quality')
    }
}

export function qualityPresetToDescription(code: AudioQualityPresetCode, t: TFunction<'translation'>) {
    switch (code) {
        case 'voice':
            return t('Good for voice calls')
        case 'enhanced':
            return t('Opus up to 192 kbps')
        case 'pro_essential':
            return t('Opus 96 kbps')
        case 'pro_enhanced':
            return t('Opus 192 kbps')
        case 'pro_hd':
            return t('Opus 256 kbps')
        case 'pro_cd':
            return t('PCM 16 bit')
        case 'pro_studio':
            return t('PCM 24 bit')
        case 'pro_highest':
            return t('PCM 32 bit')
    }
}

type AudioFeedbackWarningProps = {
    open: boolean
    onClose: () => void
    onAccept: () => void
}
function AudioFeedbackWarning(props: AudioFeedbackWarningProps) {
    const { t } = useTranslation()

    return (
        <Dialog open={props.open} onClose={props.onClose} zIndex={21}>
            <div className="px-6">
                <DialogTitle>
                    <div className="flex gap-2 items-center">
                        <AlertIcon className="text-muse-warning" />
                        {t('Audio Feedback Warning')}
                    </div>
                </DialogTitle>
            </div>
            <DialogBody>
                {t(
                    "This device is currently your talkback microphone. Increasing the quality will cause audio feedback unless you're wearing headphones or using mute."
                )}
            </DialogBody>
            <DialogActionButtonsSection
                leftButtonText={t('Nevermind') || 'Nevermind'}
                onLeftButtonClicked={props.onClose}
                rightButtonText={t('Proceed') || 'Proceed'}
                onRightButtonClicked={props.onAccept}
            />
        </Dialog>
    )
}
